import { useTagsFeatureEnabled } from '@affine/core/modules/streamlain';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useService } from '@toeverything/infra';
import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useCallback, useMemo } from 'react';

import type {
  DisplayProperties,
  PageDisplayProperties,
  PageGroupByType,
} from './types';

export const displayPropertiesAtom = atomWithStorage<{
  [workspaceId: string]: DisplayProperties;
}>('allDocDisplayProperties', {});

const defaultProps: DisplayProperties = {
  groupBy: 'updatedDate',
  displayProperties: {
    bodyNotes: true,
    tags: true,
    createDate: true,
    updatedDate: true,
  },
};

export const useAllDocDisplayProperties = (): [
  DisplayProperties,
  (
    key: keyof DisplayProperties,
    value: PageGroupByType | PageDisplayProperties
  ) => void,
] => {
  const tagsEnabled = useTagsFeatureEnabled();
  const workspace = useService(WorkspaceService).workspace;
  const [properties, setProperties] = useAtom(displayPropertiesAtom);

  const workspaceProperties = properties[workspace.id] || defaultProps;
  const visibleWorkspaceProperties = useMemo<DisplayProperties>(() => {
    if (tagsEnabled) {
      return workspaceProperties;
    }
    return {
      ...workspaceProperties,
      groupBy:
        workspaceProperties.groupBy === 'tag'
          ? defaultProps.groupBy
          : workspaceProperties.groupBy,
      displayProperties: {
        ...workspaceProperties.displayProperties,
        tags: false,
      },
    };
  }, [tagsEnabled, workspaceProperties]);

  const onChange = useCallback(
    (
      key: keyof DisplayProperties,
      value: PageGroupByType | PageDisplayProperties
    ) => {
      setProperties(prev => ({
        ...prev,
        [workspace.id]: {
          ...(prev[workspace.id] || defaultProps),
          [key]: value,
        },
      }));
    },
    [setProperties, workspace.id]
  );

  return [visibleWorkspaceProperties, onChange];
};
